import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import DateInput from '../../ui/inputs/DateInput';
import Select from '../../ui/selects/Select';
import { Building, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';

const RealEstateModal = ({ clientId, realEstateId = null, scope, onClose, onSuccess }) => {
  const isEdit = !!realEstateId;
  const { addToast } = useToast();

  const [form, setForm] = useState({
    name: '',
    realEstateType: '',
    state: '',
    city: '',
    marketValue: '',
    historicValue: '',
    acquisitionDate: '',
    ownershipPercentage: '',
    sharedWithSpouse: false,
    financed: false,
    debtBalance: '',
    payoffDate: '',
    hasInsurance: false,
    hasRestriction: false,
    restrictionType: '',
    isPersonal: false,
    notes: '',
  });

  const [realEstateTypeOptions, setRealEstateTypeOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [citySearch, setCitySearch] = useState('');
  const [cityResults, setCityResults] = useState([]);

  // Fetch options
  useEffect(() => {
    fetch('/app/shared/form_options')
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);
        setRealEstateTypeOptions(camelData.realEstateTypes.map((type) => ({ label: type.label, value: type.value })));
        setStateOptions(camelData.states.map((state) => ({ label: state.label, value: state.value })));
      })
      .catch((err) => {
        console.error('Erro ao carregar dados do formulário', err);
        addToast({ type: 'error', message: err.message });
      });
  }, []);

  // Fetch imóvel (edição)
  useEffect(() => {
    if (!isEdit) return;
    fetch(`/app/planner/client/${clientId}/dashboard/properties/real_estates/${realEstateId}?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => setForm(camelizeKeys(data)))
      .catch((err) => {
        console.error('Erro ao carregar imóvel', err);
        addToast({ type: 'error', message: 'Erro ao carregar dados do imóvel' });
      });
  }, [isEdit, clientId, realEstateId]);

  // Fetch cidades
  useEffect(() => {
    if (form.state.length < 2 || citySearch.length < 2) return setCityResults([]);
    const controller = new AbortController();

    fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${form.state}/municipios`, {
      signal: controller.signal,
    })
      .then((res) => res.json())
      .then((data) => {
        const filtered = data.filter((c) => c.nome.toLowerCase().includes(citySearch.toLowerCase()));
        setCityResults(filtered.map((city) => ({ label: city.nome, value: city.nome })));
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.log('Erro ao buscar cidades:', err);
          addToast({ type: 'error', message: 'Erro ao buscar cidades do IBGE' });
        }
      });

    return () => controller.abort();
  }, [form.state, citySearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (name) => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = snakecaseKeys({ realEstateProperty: form });
    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/properties/real_estates/${realEstateId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/properties/real_estates?scope=${scope}`;
    const method = isEdit ? 'PATCH' : 'POST';
    const successMessage = isEdit ? 'Imóvel atualizado com sucesso!' : 'Imóvel cadastrado com sucesso!';

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar imóvel');
      }

      onClose();
      addToast({ type: 'success', message: successMessage });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <Building size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? 'Editar Imóvel' : 'Cadastrar Imóvel'}
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Input name="name" label="Identificação" value={form.name} onChange={handleChange} required />
          <Select
            name="realEstateType"
            label="Tipo do imóvel"
            value={form.realEstateType}
            onChange={handleChange}
            options={realEstateTypeOptions}
            required
          />
          <Select
            name="state"
            label="Estado"
            value={form.state}
            onChange={handleChange}
            options={stateOptions}
            required
          />
          <div className="relative">
            <Input
              name="city"
              label="Cidade"
              value={form.city}
              onChange={(e) => {
                handleChange(e);
                setCitySearch(e.target.value);
              }}
              autoComplete="off"
              required
            />
            {cityResults.length > 0 && (
              <ul className="absolute z-10 bg-white border border-gray-300 w-full mt-1 max-h-48 overflow-y-auto shadow-lg rounded">
                {cityResults.map((city, idx) => (
                  <li
                    key={idx}
                    className="px-4 py-2 hover:bg-tealLight cursor-pointer text-sm"
                    onClick={() => {
                      setForm((prev) => ({ ...prev, city: city.value }));
                      setCityResults([]);
                    }}
                  >
                    {city.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <CurrencyInput name="marketValue" label="Valor de mercado" value={form.marketValue} onChange={handleChange} />
          <CurrencyInput
            name="historicValue"
            label="Valor histórico"
            value={form.historicValue}
            onChange={handleChange}
          />
          <DateInput
            name="acquisitionDate"
            label="Data de aquisição"
            value={form.acquisitionDate}
            onChange={handleChange}
          />
          <Input
            name="ownershipPercentage"
            label="% de participação"
            type="number"
            value={form.ownershipPercentage}
            onChange={handleChange}
          />

          <div className="col-span-2">
            <CustomCheckbox
              labelClassName="text-base"
              name="financed"
              label="Financiado"
              checked={form.financed}
              onChange={() => handleCheckbox('financed')}
            />
          </div>
          <CurrencyInput
            name="debtBalance"
            label="Saldo devedor"
            value={form.debtBalance}
            onChange={handleChange}
            disabled={!form.financed}
          />
          <DateInput
            name="payoffDate"
            label="Data prevista de quitação"
            value={form.payoffDate}
            onChange={handleChange}
            disabled={!form.financed}
          />

          <div className="col-span-2">
            <CustomCheckbox
              labelClassName="text-base"
              name="hasRestriction"
              label="Possui restrição"
              checked={form.hasRestriction}
              onChange={() => handleCheckbox('hasRestriction')}
            />
          </div>
          <div className="col-span-2">
            <Input
              name="restrictionType"
              label="Tipo de restrição"
              value={form.restrictionType}
              onChange={handleChange}
              disabled={!form.hasRestriction}
            />
          </div>

          <div className="md:col-span-2 grid grid-cols-2 gap-2">
            <CustomCheckbox
              labelClassName="text-base"
              name="sharedWithSpouse"
              label="O restante do imóvel é do cônjuge"
              checked={form.sharedWithSpouse}
              onChange={() => handleCheckbox('sharedWithSpouse')}
            />
            <CustomCheckbox
              labelClassName="text-base"
              name="hasInsurance"
              label="Possui seguro prestamista"
              checked={form.hasInsurance}
              onChange={() => handleCheckbox('hasInsurance')}
            />
            <CustomCheckbox
              labelClassName="text-base"
              name="isPersonal"
              label="É um bem particular"
              checked={form.isPersonal}
              onChange={() => handleCheckbox('isPersonal')}
            />
          </div>

          <div className="md:col-span-2 flex justify-end items-center mt-1">
            <Button type="submit">{isEdit ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR IMÓVEL'}</Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RealEstateModal;
